/**
 * 任务相关的 API 路由
 */

import { Router } from 'express';
import { TaskService } from '../services/task-service.js';
import { ApiError, ApiErrorCode, TaskQueryParams } from '../types.js';
import { logger } from '../../utils/logger.js';

const router = Router();
const taskService = new TaskService();

// 获取所有任务
router.get('/', async (req, res, next) => {
  try {
    const query = req.query as TaskQueryParams;
    
    // 获取所有任务
    let tasks = await taskService.list();
    
    // 应用过滤
    if (query.status) {
      tasks = tasks.filter(t => t.status === query.status);
    }
    if (query.priority) {
      tasks = tasks.filter(t => t.priority === query.priority);
    }
    if (query.tags) {
      const tags = Array.isArray(query.tags) ? query.tags : [query.tags];
      tasks = tasks.filter(t => tags.some(tag => t.tags.includes(tag)));
    }
    if (query.search) {
      const search = query.search.toLowerCase();
      tasks = tasks.filter(t => 
        t.content.toLowerCase().includes(search)
      );
    }
    
    // 分页
    const page = parseInt(query.page as any) || 1;
    const limit = parseInt(query.limit as any) || 20;
    const start = (page - 1) * limit;
    const end = start + limit;
    const total = tasks.length;
    const paginatedTasks = tasks.slice(start, end);
    
    // 转换任务格式，添加 title 字段以兼容前端
    const transformedTasks = paginatedTasks.map(task => ({
      ...task,
      title: task.content,  // 添加 title 字段映射
      name: task.content    // 添加 name 字段映射
    }));
    
    res.paginate(transformedTasks, {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
      hasNext: end < total,
      hasPrev: page > 1,
    });
  } catch (error) {
    next(error);
  }
});

// 获取单个任务
router.get('/:id', async (req, res, next) => {
  try {
    const { id } = req.params;
    const task = await taskService.get(id);
    
    if (!task) {
      throw new ApiError(
        ApiErrorCode.TASK_NOT_FOUND,
        `Task with id ${id} not found`,
        404
      );
    }
    
    // 转换任务格式，添加 title 字段以兼容前端
    const transformedTask = {
      ...task,
      title: task.content,  // 添加 title 字段映射
      name: task.content    // 添加 name 字段映射
    };
    
    res.success(transformedTask);
  } catch (error) {
    next(error);
  }
});

// 创建任务
router.post('/', async (req, res, next) => {
  try {
    const taskData = req.body;
    
    // 验证必填字段
    if (!taskData.title && !taskData.content) {
      throw new ApiError(
        ApiErrorCode.VALIDATION_ERROR,
        'Title or content is required',
        400
      );
    }
    
    // 创建任务
    const newTask = await taskService.create({
      content: taskData.title || taskData.content,
      status: taskData.status || 'todo',
      priority: taskData.priority || 'medium',
      type: taskData.type,
      tags: taskData.tags || []
    });
    logger.info(`Task created: ${newTask.id}`);
    
    res.success(newTask, { created: true });
  } catch (error) {
    next(error);
  }
});

// 更新任务
router.put('/:id', async (req, res, next) => {
  try {
    const { id } = req.params;
    const updates = req.body;
    
    const updatedTask = await taskService.update(id, updates);
    logger.info(`Task updated: ${id}`);
    
    res.success(updatedTask);
  } catch (error) {
    next(error);
  }
});

// 删除任务
router.delete('/:id', async (req, res, next) => {
  try {
    const { id } = req.params;
    
    await taskService.delete(id);
    logger.info(`Task deleted: ${id}`);
    
    res.success({ deleted: true, id });
  } catch (error) {
    next(error);
  }
});

// 批量操作
router.post('/batch', async (req, res, next) => {
  try {
    const { operation, taskIds, updates } = req.body;
    
    if (!operation || !taskIds || !Array.isArray(taskIds)) {
      throw new ApiError(
        ApiErrorCode.VALIDATION_ERROR,
        'Operation and taskIds are required',
        400
      );
    }
    
    const results = [];
    
    switch (operation) {
      case 'update':
        for (const id of taskIds) {
          try {
            await taskService.update(id, updates);
            results.push({ id, success: true });
          } catch (error) {
            results.push({ id, success: false, error: (error as Error).message });
          }
        }
        break;
        
      case 'delete':
        for (const id of taskIds) {
          try {
            await taskService.delete(id);
            results.push({ id, success: true });
          } catch (error) {
            results.push({ id, success: false, error: (error as Error).message });
          }
        }
        break;
        
      default:
        throw new ApiError(
          ApiErrorCode.VALIDATION_ERROR,
          `Invalid operation: ${operation}`,
          400
        );
    }
    
    res.success({
      operation,
      results,
      summary: {
        total: results.length,
        success: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length,
      },
    });
  } catch (error) {
    next(error);
  }
});

// 扩展任务端点：按 PRD 获取任务
router.get('/by-prd/:prdId', async (req, res, next) => {
  try {
    const { prdId } = req.params;
    const tasks = await taskService.getByPrdId(prdId);
    
    res.success({
      prdId,
      tasks,
      count: tasks.length
    });
  } catch (error) {
    next(error);
  }
});

// 扩展任务端点：按设计文档获取任务
router.get('/by-design/:designId', async (req, res, next) => {
  try {
    const { designId } = req.params;
    const tasks = await taskService.getByDesignId(designId);
    
    res.success({
      designId,
      tasks,
      count: tasks.length
    });
  } catch (error) {
    next(error);
  }
});

// 扩展任务端点：按工作流获取任务
router.get('/by-workflow/:workflowId', async (req, res, next) => {
  try {
    const { workflowId } = req.params;
    const tasks = await taskService.getByWorkflowId(workflowId);
    
    res.success({
      workflowId,
      tasks,
      count: tasks.length
    });
  } catch (error) {
    next(error);
  }
});

// 扩展任务端点：按 Agent 获取任务
router.get('/by-agent/:agentName', async (req, res, next) => {
  try {
    const { agentName } = req.params;
    const tasks = await taskService.getByAgent(agentName);
    
    res.success({
      agentName,
      tasks,
      count: tasks.length
    });
  } catch (error) {
    next(error);
  }
});

// 扩展任务端点：按技术栈获取任务
router.get('/by-tech/:tech', async (req, res, next) => {
  try {
    const { tech } = req.params;
    const tasks = await taskService.getByTechStack(tech);
    
    res.success({
      tech,
      tasks,
      count: tasks.length
    });
  } catch (error) {
    next(error);
  }
});

// 扩展任务端点：获取扩展任务详情
router.get('/:id/extended', async (req, res, next) => {
  try {
    const { id } = req.params;
    const extendedTask = await taskService.getExtended(id);
    
    if (!extendedTask) {
      throw new ApiError(
        ApiErrorCode.TASK_NOT_FOUND,
        `Task with id ${id} not found`,
        404
      );
    }
    
    res.success(extendedTask);
  } catch (error) {
    next(error);
  }
});

export { router as taskRouter };